import {Button, Group, Stack, Text} from "@mantine/core";
import {Fragment, useState} from "react";
import {t} from "i18next";
import classes from "./RelatedGroup.module.css";

const DEFAULT_VISIBLE_COUNT = 5;

type RelatedGroupProps<T> = {
    icon: React.ReactNode;
    title: string;
    items: T[];
    renderItem: (item: T) => React.ReactNode;
    keyExtractor: (item: T) => string;
    defaultVisibleCount?: number;
    emptyText?: string;
};

export const RelatedGroup = <T,>({
    icon,
    title,
    items,
    renderItem,
    keyExtractor,
    defaultVisibleCount = DEFAULT_VISIBLE_COUNT,
    emptyText,
}: RelatedGroupProps<T>) => {
    const [expanded, setExpanded] = useState(false);

    if (items.length === 0) {
        if (!emptyText) return null;
        return (
            <Stack gap="xs" className={classes.section}>
                <Group gap="xs" align="center" my="xs">
                    {icon}
                    <Text fw={400}>{title}</Text>
                </Group>
                <Text size="sm" c="dimmed">
                    {emptyText}
                </Text>
            </Stack>
        );
    }

    const visibleItems = expanded
        ? items
        : items.slice(0, defaultVisibleCount);

    const hasMore = items.length > defaultVisibleCount;

    return (
        <Stack gap="xs" className={classes.section}>
            <Group gap="xs" align="center" my="xs">
                {icon}
                <Text fw={400}>{title}</Text>
            </Group>
            <Stack gap={1}>
                {visibleItems.map((item) => (
                    <Fragment key={keyExtractor(item)}>{renderItem(item)}</Fragment>
                ))}
            </Stack>
            {hasMore && (
                <Button
                    variant="subtle"
                    size="xs"
                    color="gray"
                    onClick={() => setExpanded(!expanded)}
                    className={classes.expandButton}
                >
                    {expanded
                        ? t("common.showLess")
                        : `${t("common.seeAll")} (${items.length})`}
                </Button>
            )}
        </Stack>
    );
};
